import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { useQuery } from '@apollo/client/react';
import { GET_VISIBLE_BIDS } from '../../../graphql/queries/visibleBids';
import { GET_VISIBLE_TRANSFER_OFFERS } from '../../../graphql/queries/visibleTransferOffers';
import { GET_FANTASY_LEAGUE_MEMBERS } from '../../../graphql/queries/fantasyLeagueMembers';
import type {
  GetVisibleBidsQuery,
  GetVisibleBidsQueryVariables,
  GetVisibleTransferOffersQuery,
  GetVisibleTransferOffersQueryVariables,
  GetFantasyLeagueMembersQuery,
  GetFantasyLeagueMembersQueryVariables,
} from '../../../types/graphql-generated';
import { useUserAccount } from '../../../hooks/useUserAccount';
import { BidReviewModal } from './BidReviewModal';
import { ReviewItemFactory } from './reviewItem';
import type { ReviewItem } from './reviewItem';
import { ReceivedOffersList, type OfferGroup } from './ReceivedOffersList';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';

type VisibleBid = GetVisibleBidsQuery['visibleBids'][number];
type VisibleTransferOffer = GetVisibleTransferOffersQuery['visibleTransferOffers'][number];

interface ReceivedBidsPageProps {
  leagueId: string;
}

export const ReceivedBidsPage = ({ leagueId }: ReceivedBidsPageProps) => {
  const { userAccount } = useUserAccount();
  const navigate = useNavigate();

  const [selectedItem, setSelectedItem] = useState<ReviewItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: bidsData, loading: bidsLoading, error: bidsError } = useQuery<
    GetVisibleBidsQuery,
    GetVisibleBidsQueryVariables
  >(GET_VISIBLE_BIDS, {
    variables: { fantasyLeagueId: leagueId },
    pollInterval: 60000,
  });

  const { data: membersData, loading: membersLoading, error: membersError } = useQuery<
    GetFantasyLeagueMembersQuery,
    GetFantasyLeagueMembersQueryVariables
  >(GET_FANTASY_LEAGUE_MEMBERS, {
    variables: { id: leagueId },
    pollInterval: 60000,
  });

  const { data: transferOffersData, loading: transferOffersLoading, error: transferOffersError } = useQuery<
    GetVisibleTransferOffersQuery,
    GetVisibleTransferOffersQueryVariables
  >(GET_VISIBLE_TRANSFER_OFFERS, {
    variables: { fantasyLeagueId: leagueId },
    pollInterval: 60000,
  });

  const handleOpenModal = (item: ReviewItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const loading = bidsLoading || membersLoading || transferOffersLoading;
  const error = bidsError || membersError || transferOffersError;
  const visibleBids = bidsData?.visibleBids || [];
  const visibleTransferOffers = transferOffersData?.visibleTransferOffers || [];

  const currentUserMember = membersData?.fantasyLeague?.members.find(
    (member) => member.user.id === userAccount?.id
  );

  // --- Market bids: group by player ---
  const bidsForMyPlayers = visibleBids.filter(
    (bid) => bid.marketEntry.seller?.id === currentUserMember?.id
  );

  const bidGroupsByPlayer = bidsForMyPlayers.reduce((acc, bid) => {
    const playerId = bid.marketEntry.player.id;
    if (!acc[playerId]) {
      acc[playerId] = { player: bid.marketEntry.player, bids: [] };
    }
    acc[playerId].bids.push(bid);
    return acc;
  }, {} as Record<string, { player: typeof bidsForMyPlayers[0]['marketEntry']['player']; bids: typeof bidsForMyPlayers }>);

  const marketOfferGroups: OfferGroup[] = Object.values(bidGroupsByPlayer).map(({ player, bids }) => ({
    player,
    offers: bids.map((bid: VisibleBid) => ({
      id: bid.id,
      amount: bid.amount,
      bidderName: bid.bidder?.teamName || 'Futyclub',
      bidderMemberId: bid.bidder?.id,
      onReview: () => handleOpenModal(ReviewItemFactory.fromBid(bid)),
    })),
  }));

  // --- Transfer offers: group by player ---
  const transferOffersForMe = visibleTransferOffers.filter(
    (offer) => offer.seller?.id === currentUserMember?.id
  );

  const transferOfferGroupsByPlayer = transferOffersForMe.reduce((acc, offer) => {
    const playerId = offer.player.id;
    if (!acc[playerId]) {
      acc[playerId] = { player: offer.player, offers: [] };
    }
    acc[playerId].offers.push(offer);
    return acc;
  }, {} as Record<string, { player: VisibleTransferOffer['player']; offers: VisibleTransferOffer[] }>);

  const transferOfferGroups: OfferGroup[] = Object.values(transferOfferGroupsByPlayer).map(({ player, offers }) => ({
    player,
    offers: offers.map((offer: VisibleTransferOffer) => ({
      id: offer.id,
      amount: offer.amount,
      bidderName: offer.bidder.teamName ?? offer.bidder.id,
      bidderMemberId: offer.bidder.id,
      onReview: () => handleOpenModal(ReviewItemFactory.fromTransferOffer(offer)),
    })),
  }));

  const hasAnyOffers = marketOfferGroups.length > 0 || transferOfferGroups.length > 0;
  const handleMemberClick = (fantasyLeagueMemberId: string) => {
    navigate(`/app/ligas/${leagueId}/mercado/plantilla/${fantasyLeagueMemberId}`);
  };

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/app/ligas/${leagueId}/mercado`} />
          </IonButtons>
          <IonTitle>Ofertas recibidas</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">

        {loading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>Error al cargar las ofertas</p>
          </IonText>
        )}

        {!loading && !hasAnyOffers && (
          <IonText>
            <div className="text-center mt-8">
              <h2 className="text-xl font-semibold">No tienes ofertas recibidas</h2>
              <p className="text-gray-500 mt-2">
                Cuando otros usuarios hagan ofertas por tus jugadores aparecerán aquí
              </p>
            </div>
          </IonText>
        )}

        {!loading && (
          <div className="space-y-6">
            <ReceivedOffersList
              title="Ofertas por tus jugadores en el mercado"
              subtitle={
                marketOfferGroups.length > 0
                  ? `${marketOfferGroups.length} ${marketOfferGroups.length === 1 ? 'jugador' : 'jugadores'} con ofertas`
                  : undefined
              }
              groups={marketOfferGroups}
              onMemberClick={handleMemberClick}
            />

            <ReceivedOffersList
              title="Ofertas directas recibidas"
              subtitle={
                transferOfferGroups.length > 0
                  ? `${transferOfferGroups.length} ${transferOfferGroups.length === 1 ? 'jugador' : 'jugadores'} con ofertas`
                  : undefined
              }
              groups={transferOfferGroups}
              emptyMessage="No tienes ofertas directas recibidas"
              onMemberClick={handleMemberClick}
            />
          </div>
        )}

        {selectedItem && (
          <BidReviewModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            item={selectedItem}
            leagueId={leagueId}
            onSuccess={() => {}}
          />
        )}
      </IonContent>
    </IonPage>
  );
};
